import { Map as ImmutableMap, List } from 'immutable';
import { memoizeWithArgs } from 'proxy-memoize';

import React from 'react';

import { categoriesActions, categoriesTypes } from 'app-components/flux/categories';
import { actionHelpers } from 'app-components/flux/core';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';

import Typography from '@mui/material/Typography';
import {
  DIALOG_TYPE as DIALOG_NOTIFICATION,
  mkNotificationDialogProps,
} from 'components/Dialogs/NotificationDialog/types';

export const getIdAndDescendantIds = memoizeWithArgs((category, categoriesByParentId) => {
  let ids = List([category.id]);
  const children = categoriesByParentId?.get(category.id) || [];
  children.forEach((feCategory) => {
    ids = ids.concat(getIdAndDescendantIds(feCategory, categoriesByParentId));
  });
  return ids;
});

// get the depth of the given category in the heirarchy
export const getDepthOfCategory = (category, categoriesById) => {
  if (!category) return 0;

  let visited = new Set(); // Prevent infinite loops
  let depth = 1; // Start at 1 (root level)

  let fCategory = category;
  while (fCategory.parentId !== '0') {
    if (visited.has(fCategory.id)) break; // Prevent cyclic loops
    visited.add(fCategory.id);

    fCategory = categoriesById.get(fCategory.parentId);
    if (!fCategory) break; // If parent doesn't exist, stop to avoid errors

    depth = depth + 1;
  }
  return depth;
};

// get the distance to the furthest leaf node
export const getMaxDepthOfDescendants = (category, categoriesByParentId) => {
  const childCategories = categoriesByParentId.get(category.id);
  if (!childCategories) {
    return 0;
  }
  let maxChildDepth = 0;
  childCategories.forEach((child) => {
    const childDepth = getMaxDepthOfDescendants(child, categoriesByParentId);
    maxChildDepth = Math.max(maxChildDepth, childDepth);
  });
  return maxChildDepth + 1;
};

export const hasDuplicateName = (name, siblingCategories) => {
  const duplicateCategory = siblingCategories.find(
    (siblingCategory) =>
      siblingCategory.name.localeCompare(name, undefined, { numeric: true, sensitivity: 'base' }) === 0,
  );
  return Boolean(duplicateCategory);
};

export const hasSameNameAsParent = (name, parentId, categories) => {
  const parentWithSameName = categories.find(
    (category) => category.id === parentId && category.name?.toLowerCase().trim() === name?.toLowerCase().trim(),
  );
  return Boolean(parentWithSameName);
};

export const updateCategory = async (category, params = {}, dispatch) => {
  const options = { id: category.id, ...params };

  const getErrorContent = (error) => (
    <Typography variant='titleMedium'>
      {error.response?.data?.errors[0]?.detail ||
        error.response?.data?.errors[0]?.title ||
        'Sorry, there seems to be a problem on our end. Please try again later.'}
      {error?.response?.status && <Typography>{`Error Code: ${error?.response?.status}`}</Typography>}
    </Typography>
  );

  const updateCategoryAction = actionHelpers.bindPromiseAction(dispatch, categoriesActions.updateCategory);
  try {
    await updateCategoryAction(categoriesTypes.mkCategory(options));
  } catch (error) {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'error-update-category-dialog',
          type: DIALOG_NOTIFICATION,
          props: ImmutableMap(
            mkNotificationDialogProps({
              title: 'Update category failure',
              content: getErrorContent(error),
            }),
          ),
        }),
      ),
    );
  }
};

// convert to initial caps
export const initialCaps = (input) => {
  return input
    ? input
        .toLowerCase()
        .replace(/(?:^|_)(\w)/g, (match, p1) => ` ${p1.toUpperCase()}`)
        .trim()
    : input;
};
